import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, Settings, Ticket, LogOut, PanelLeftClose, PanelLeftOpen, School, Building2, UserCog, Tag, Layers, Network, BookOpenText, Zap, Mail, ChevronDown, GraduationCap } from 'lucide-react';
import { useUIStore } from '@/shared/stores/uiStore';
import { useAuthStore, UserRole } from '@/shared/stores/authStore';
import env from '@/core/config/env';
import { getMediaUrl } from '@/shared/utils/media';
import raabtaLogo from '@/assets/raabtalogo_white_bg.png';
import './Sidebar.css';

interface SidebarProps {
    onLogout: () => void;
}

interface NavItem {
    path?: string;
    label: string;
    icon: React.ReactNode;
    allowedRoles: UserRole[];
    children?: {
        path: string;
        label: string;
        icon: React.ReactNode;
    }[];
}

export const Sidebar: React.FC<SidebarProps> = ({ onLogout }) => {
    const location = useLocation();
    const { isSidebarOpen, toggleSidebar } = useUIStore();
    const { user, tenant } = useAuthStore();
    const isSuperAdmin = user?.role === 'SUPER_ADMIN';
    const appName = isSuperAdmin ? 'Raabta' : (tenant?.name || env.APP_NAME || 'Raabta');
    const firstLetter = appName.charAt(0).toUpperCase();
    const logoUrl = isSuperAdmin ? null : getMediaUrl(tenant?.logo_url || tenant?.logo);
    const slug = tenant?.slug || 'school';

    const isSettingsActive = location.pathname.includes('/school-admin/settings') || location.pathname.includes('/school-admin/email-settings');
    const [isSettingsExpanded, setIsSettingsExpanded] = React.useState(isSettingsActive);

    React.useEffect(() => {
        if (isSettingsActive) {
            setIsSettingsExpanded(true);
        }
    }, [location.pathname, isSettingsActive]);

    const getRoleLabel = (role?: string) => {
        if (!role) return 'User';
        switch (role.toUpperCase()) {
            case 'SUPER_ADMIN': return 'Super Admin';
            case 'SCHOOL_ADMIN': return 'School Admin';
            case 'EMPLOYEE': return 'Staff Member';
            default: return role;
        }
    };

    const allNavItems: NavItem[] = [
        // Super Admin Items
        { path: '/super-admin', label: 'Global Dashboard', icon: <LayoutDashboard size={20} />, allowedRoles: ['SUPER_ADMIN'] },
        { path: '/super-admin/tenants', label: 'Manage Schools', icon: <School size={20} />, allowedRoles: ['SUPER_ADMIN'] },
        { path: '/super-admin/contacts', label: 'Contact Messages', icon: <Mail size={20} />, allowedRoles: ['SUPER_ADMIN'] },

        // School Admin Items
        { path: `/${slug}/school-admin`, label: 'School Dashboard', icon: <LayoutDashboard size={20} />, allowedRoles: ['SCHOOL_ADMIN'] },
        { path: `/${slug}/tickets`, label: 'Ticketing Portal', icon: <Ticket size={20} />, allowedRoles: ['SCHOOL_ADMIN'] },
        { path: `/${slug}/school-admin/knowledge-hub`, label: 'Knowledge Hub', icon: <BookOpenText size={20} />, allowedRoles: ['SCHOOL_ADMIN'] },
        { path: `/${slug}/school-admin/departments`, label: 'Departments', icon: <Building2 size={20} />, allowedRoles: ['SCHOOL_ADMIN'] },
        { path: `/${slug}/school-admin/employees`, label: 'Employees', icon: <UserCog size={20} />, allowedRoles: ['SCHOOL_ADMIN'] },
        { path: `/${slug}/school-admin/students`, label: 'Students', icon: <GraduationCap size={20} />, allowedRoles: ['SCHOOL_ADMIN'] },
        { path: `/${slug}/school-admin/parents`, label: 'Parents', icon: <Users size={20} />, allowedRoles: ['SCHOOL_ADMIN'] },
        { path: `/${slug}/school-admin/workflows`, label: 'Workflows', icon: <Network size={20} />, allowedRoles: ['SCHOOL_ADMIN'] },
        { path: `/${slug}/school-admin/tags`, label: 'Tags', icon: <Tag size={20} />, allowedRoles: ['SCHOOL_ADMIN'] },
        { path: `/${slug}/school-admin/classes`, label: 'Classes', icon: <Layers size={20} />, allowedRoles: ['SCHOOL_ADMIN'] },

        // Employee Items
        { path: '/employee', label: 'My Workspace', icon: <LayoutDashboard size={20} />, allowedRoles: ['EMPLOYEE'] },
        { path: `/${slug}/tickets`, label: 'Ticketing Portal', icon: <Ticket size={20} />, allowedRoles: ['EMPLOYEE'] },

        // Settings (School Admin)
        {
            label: 'Settings',
            icon: <Settings size={20} />,
            allowedRoles: ['SCHOOL_ADMIN'],
            children: [
                { path: `/${slug}/school-admin/settings`, label: 'School Settings', icon: <Building2 size={16} /> },
                { path: `/${slug}/school-admin/email-settings`, label: 'Email Settings', icon: <Mail size={16} /> },
            ]
        },
    ];

    const navItems = allNavItems.filter(item => user && item.allowedRoles.includes(user.role));

    return (
        <>
            {/* Mobile Overlay */}
            {isSidebarOpen && (
                <div className="app-sidebar__overlay lg:hidden" onClick={toggleSidebar} />
            )}

            <aside className={`app-sidebar ${!isSidebarOpen ? 'app-sidebar--closed' : ''}`}>
                {/* Floating Toggle Button */}
                <button
                    className="app-sidebar__toggle-btn"
                    onClick={toggleSidebar}
                    title={isSidebarOpen ? "Collapse Sidebar" : "Expand Sidebar"}
                >
                    {isSidebarOpen ? <PanelLeftClose size={18} /> : <PanelLeftOpen size={18} />}
                </button>

                <div className="app-sidebar__header">
                    {/* User Details */}
                    <div className="app-sidebar__user-section">
                        <div
                            className="app-sidebar__user-avatar"
                            style={isSuperAdmin || ((user?.role === 'SCHOOL_ADMIN' || user?.role === 'EMPLOYEE') && logoUrl) ? { background: 'transparent', border: 'none', boxShadow: 'none' } : undefined}
                        >
                            {isSuperAdmin ? (
                                <img src={raabtaLogo} alt="Raabta" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'contain' }} />
                            ) : (user?.role === 'SCHOOL_ADMIN' || user?.role === 'EMPLOYEE') && logoUrl ? (
                                <img src={logoUrl} alt={tenant?.name || 'School Logo'} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'contain' }} />
                            ) : user?.avatar && !user.avatar.includes('ui-avatars.com') ? (
                                <img src={user.avatar} alt={user?.name} style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                            ) : (
                                <span>{user?.name ? user.name.charAt(0).toUpperCase() : 'U'}</span>
                            )}
                        </div>
                        <div className="app-sidebar__user-info">
                            <span className="app-sidebar__user-name">{user?.name || 'User'}</span>
                            <span className="app-sidebar__user-email">{user?.email || ''}</span>
                            <span className="app-sidebar__user-role">{getRoleLabel(user?.role)}</span>
                        </div>
                    </div>
                </div>

                <nav className="app-sidebar__nav">
                    <ul className="app-sidebar__list">
                        {navItems.map((item) => {
                            if (item.children) {
                                const isChildActive = item.children.some(child => location.pathname === child.path);
                                return (
                                    <li key={item.label} className="app-sidebar__item-with-children">
                                        <button
                                            onClick={() => {
                                                if (!isSidebarOpen) {
                                                    toggleSidebar();
                                                    setIsSettingsExpanded(true);
                                                } else {
                                                    setIsSettingsExpanded(!isSettingsExpanded);
                                                }
                                            }}
                                            className={`app-sidebar__link ${isChildActive ? 'parent-active' : ''}`}
                                            style={{
                                                width: '100%',
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                textAlign: 'left',
                                                display: 'flex',
                                                alignItems: 'center'
                                            }}
                                        >
                                            {item.icon}
                                            <span className="app-sidebar__label" style={{ flex: 1, marginLeft: '0.75rem', textAlign: 'left' }}>{item.label}</span>
                                            {isSidebarOpen && (
                                                <ChevronDown
                                                    size={16}
                                                    style={{
                                                        transform: isSettingsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                        transition: 'transform 0.2s'
                                                    }}
                                                />
                                            )}
                                        </button>
                                        {isSettingsExpanded && isSidebarOpen && (
                                            <ul className="app-sidebar__sub-list">
                                                {item.children.map((child) => (
                                                    <li key={child.path}>
                                                        <Link
                                                            to={child.path}
                                                            className={`app-sidebar__sub-link ${location.pathname === child.path ? 'active' : ''}`}
                                                        >
                                                            {child.icon}
                                                            <span className="app-sidebar__sub-label">{child.label}</span>
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </li>
                                );
                            }
                            return (
                                <li key={item.path || item.label}>
                                    <Link
                                        to={item.path || '#'}
                                        className={`app-sidebar__link ${location.pathname === item.path ? 'active' : ''}`}
                                    >
                                        {item.icon}
                                        <span className="app-sidebar__label">{item.label}</span>
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </nav>

                <button className="app-sidebar__logout" onClick={onLogout}>
                    <LogOut size={20} />
                    <span className="app-sidebar__label">Logout</span>
                </button>
            </aside>
        </>
    );
};
